import { useState, type KeyboardEvent } from 'react';
import { formatCopy } from '@/i18n/ns';
import { dayTicks, niceScale, type Formatters } from './format';
import type { UsageCopy } from './copy';

export interface Series {
  key: string;
  label: string;
  color: string;
}

export interface BarChartProps {
  series: Series[];
  days: string[];
  /** Credits per day, one value per series. */
  values: number[][];
  today: string;
  /** First day index shown as out of credits, or -1. */
  outFrom?: number;
  /** Series to keep at full strength; the others fade. */
  highlight?: number;
  f: Formatters;
  copy: UsageCopy;
}

function Tooltip({ props, index }: { props: BarChartProps; index: number }) {
  const { series, days, values, today, f, copy } = props;
  const x = ((index + 0.5) / days.length) * 100;
  const total = values[index].reduce((sum, value) => sum + value, 0);
  const style =
    x < 55
      ? { left: `calc(${x.toFixed(2)}% + 18px)` }
      : { right: `calc(${(100 - x).toFixed(2)}% + 18px)` };
  return (
    <div className="cu-tip" style={style} role="status">
      <div className="cu-tip-date">
        {days[index] === today
          ? formatCopy(copy.soFar, { day: f.longDay(days[index]) })
          : f.longDay(days[index])}
      </div>
      {series.map((item, j) => (
        <div className="cu-tip-row" key={item.key}>
          <span className="cu-tip-key" style={{ background: item.color }} />
          <span className="cu-tip-val">{f.amount(values[index][j])}</span>
          <span className="cu-tip-lab">{item.label}</span>
        </div>
      ))}
      <div className="cu-tip-row cu-tip-total">
        <span className="cu-tip-key" />
        <span className="cu-tip-val">{f.amount(total)}</span>
        <span className="cu-tip-lab">{copy.creditsInTotal}</span>
      </div>
    </div>
  );
}

/** Daily stacked bars with a keyboard and pointer tooltip (DESIGN-SPEC charts). */
export function BarChart(props: BarChartProps) {
  const { series, days, values, today, outFrom = -1, highlight = -1, f, copy } = props;
  const [hover, setHover] = useState(-1);
  const n = days.length;
  const totals = values.map((row) => row.reduce((sum, value) => sum + value, 0));
  const scale = niceScale(Math.max(0, ...totals), 4);
  const move = (direction: number) =>
    setHover((current) => {
      const next = current < 0 ? (direction > 0 ? 0 : n - 1) : current + direction;
      return Math.max(0, Math.min(n - 1, next));
    });
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      move(event.key === 'ArrowRight' ? 1 : -1);
    } else if (event.key === 'Escape') setHover(-1);
  };
  const ticks = dayTicks(days, (i) => ((i + 0.5) / n) * 100, f);
  return (
    <div
      className="cu-chart"
      tabIndex={0}
      role="group"
      aria-label={
        n ? formatCopy(copy.chartAria, { from: f.day(days[0]), to: f.day(days[n - 1]) }) : undefined
      }
      onKeyDown={onKeyDown}
      onMouseLeave={() => setHover(-1)}
      onBlur={() => setHover(-1)}
    >
      <div className="cu-plot">
        {scale.ticks.map((tick) => (
          <div
            key={tick}
            className={`cu-grid${tick === 0 ? ' is-base' : ''}`}
            style={{ bottom: `${(tick / scale.top) * 100}%` }}
          >
            <span className="cu-grid-label">{f.int(tick)}</span>
          </div>
        ))}
        {outFrom >= 0 && outFrom < n && (
          <div className="cu-band" style={{ left: `${(outFrom / n) * 100}%`, right: 0 }}>
            <span className="cu-band-label">{copy.outOfCredits}</span>
          </div>
        )}
        <div className={`cu-bars${hover >= 0 ? ' is-hovering' : ''}`}>
          {days.map((day, i) => (
            <div
              key={day}
              className={`cu-col${i === hover ? ' is-hover' : ''}${day === today ? ' is-today' : ''}`}
              onMouseEnter={() => setHover(i)}
            >
              <div
                className="cu-bar"
                style={{
                  height: `${(totals[i] / scale.top) * 100}%`,
                  animationDelay: `${Math.min(i * 14, 520)}ms`,
                }}
              >
                {values[i].map((value, j) =>
                  value > 0 ? (
                    <span
                      key={series[j].key}
                      className={`cu-seg${highlight >= 0 && highlight !== j ? ' is-faded' : ''}`}
                      style={{ flexGrow: value, background: series[j].color }}
                    />
                  ) : null,
                )}
              </div>
            </div>
          ))}
        </div>
        {hover >= 0 && hover < n && <Tooltip props={props} index={hover} />}
      </div>
      <div className="cu-xaxis" aria-hidden="true">
        {ticks.map((tick) => (
          <span
            key={`${tick.label}-${tick.pos}`}
            className={[tick.edge ? `is-${tick.edge}` : '', tick.minor ? 'is-minor' : '']
              .filter(Boolean)
              .join(' ')}
            style={{ left: `${tick.pos.toFixed(2)}%` }}
          >
            {tick.label}
          </span>
        ))}
      </div>
    </div>
  );
}
